import React from 'react';
import { motion } from 'motion/react';

interface BlurTextProps {
  text: string;
  delay?: number;
  animateBy?: 'words' | 'letters';
  direction?: 'top' | 'bottom';
  onAnimationComplete?: () => void;
  className?: string;
  /**
   * Keep the whole text on one line instead of letting the words wrap. Only
   * safe where the type is sized to fit (see `useFitToWidth`); otherwise the
   * text simply overflows.
   */
  nowrap?: boolean;
}

const BlurText: React.FC<BlurTextProps> = ({
  text,
  delay = 200,
  animateBy = 'words',
  direction = 'top',
  onAnimationComplete,
  className = '',
  nowrap = false,
}) => {
  const elements = animateBy === 'words' ? text.split(' ') : text.split('');

  return (
    <div className={`flex ${nowrap ? 'flex-nowrap' : 'flex-wrap'} ${className}`}>
      {elements.map((el, i) => (
        <motion.span
          key={i}
          initial={{ filter: 'blur(10px)', opacity: 0, y: direction === 'top' ? -20 : 20 }}
          animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: (i * delay) / 1000,
            ease: [0.22, 1, 0.36, 1],
          }}
          onAnimationComplete={i === elements.length - 1 ? onAnimationComplete : undefined}
          // `shrink-0` matters only in nowrap mode: flex items shrink by
          // default, and a shrunken word breaks its own text mid-word rather
          // than overflowing. Overflowing is the better failure.
          className={`inline-block${nowrap ? ' shrink-0 whitespace-pre' : ''}`}
          style={{ marginRight: animateBy === 'words' ? '0.3em' : '0' }}
        >
          {el === '' ? '\u00A0' : el}
        </motion.span>
      ))}
    </div>
  );
};

export default BlurText;
